const PDF_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const DOCUMENT_BG = '#ffffff';

/**
 * Ensures all images within the element have resolved before capturing.
 * Uses a safety timeout per image so it never hangs indefinitely.
 */
const waitForImagesToLoad = async (element: HTMLElement): Promise<void> => {
  const images = Array.from(element.querySelectorAll('img'));
  if (images.length === 0) return;

  await Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete && img.naturalWidth !== 0) {
            resolve();
            return;
          }
          let settled = false;
          const done = () => {
            if (!settled) {
              settled = true;
              resolve();
            }
          };
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
          // Max wait 1.2s per image - never hang execution
          setTimeout(done, 1200);
        })
    )
  );
};

export const exportElementAsPDF = async (
  element: HTMLElement,
  _filename?: string
): Promise<Blob> => {
  const html2canvas = (await import('html2canvas')).default;
  const { jsPDF } = await import('jspdf');

  // 1. Wait for images with safety timeout
  await waitForImagesToLoad(element);

  // 2. Render high-resolution canvas with useCORS (NO allowTaint to prevent SecurityError)
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: DOCUMENT_BG,
    height: element.scrollHeight,
    windowHeight: element.scrollHeight,
    logging: false,
  });

  const imgData = canvas.toDataURL('image/jpeg', 0.95);
  const canvasWidth = canvas.width || 1;
  const canvasHeight = canvas.height || 1;
  const pdfHeight = (canvasHeight * PDF_WIDTH_MM) / canvasWidth;

  let pdf: InstanceType<Awaited<typeof import('jspdf')>['jsPDF']>;

  if (pdfHeight <= A4_HEIGHT_MM + 2) {
    const isShortPage = pdfHeight < PDF_WIDTH_MM;
    pdf = new jsPDF({
      orientation: isShortPage ? 'l' : 'p',
      unit: 'mm',
      format: [PDF_WIDTH_MM, Math.max(pdfHeight, 50)],
    });
    pdf.addImage(imgData, 'JPEG', 0, 0, PDF_WIDTH_MM, pdfHeight, undefined, 'FAST');
  } else {
    // Multi-page document
    pdf = new jsPDF('p', 'mm', 'a4');
    let position = 0;
    pdf.addImage(imgData, 'JPEG', 0, position, PDF_WIDTH_MM, pdfHeight, undefined, 'FAST');

    let heightLeft = pdfHeight - A4_HEIGHT_MM;
    while (heightLeft > 2) {
      position -= A4_HEIGHT_MM;
      pdf.addPage('a4', 'p');
      pdf.addImage(imgData, 'JPEG', 0, position, PDF_WIDTH_MM, pdfHeight, undefined, 'FAST');
      heightLeft -= A4_HEIGHT_MM;
    }
  }

  return pdf.output('blob');
};

export const downloadElementAsPDF = async (
  element: HTMLElement,
  filename: string
): Promise<void> => {
  const blob = await exportElementAsPDF(element, filename);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};
